/**
 * Icon set.
 *
 * Authored SVG on one 16-unit grid at a single 1.5 stroke weight, because a
 * mixed-weight icon set is the fastest way to make a dense instrument panel
 * look assembled rather than built. Emoji and Unicode glyphs are not icons and
 * do not appear anywhere in this console.
 */

export type IconName =
  | 'pulse'
  | 'gauge'
  | 'users'
  | 'phone'
  | 'phoneIn'
  | 'phoneOff'
  | 'forward'
  | 'list'
  | 'sliders'
  | 'alert'
  | 'pause'
  | 'play'
  | 'refresh'
  | 'check'
  | 'x'
  | 'chevronRight'
  | 'sun'
  | 'moon'
  | 'external'
  | 'search'
  | 'clock'
  | 'book'
  | 'shield'
  | 'terminal'
  | 'key'
  | 'edit'
  | 'copy'
  | 'clipboard'
  | 'video'
  | 'videoOff'
  | 'mic'
  | 'micOff'
  | 'screen'
  | 'screenShare'
  | 'message'
  | 'record'
  | 'more'
  | 'userPlus'
  | 'grid'
  | 'link'
  | 'send'
  | 'volume'
  | 'expand'
  | 'signal'
  | 'file'
  | 'paperclip'
  | 'download'
  | 'trash'
  | 'headset'
  | 'eye'
  | 'hand'
  | 'blur';

const PATHS: Record<IconName, string> = {
  pulse: 'M1 8h3.2l2-4.6 2.8 9.2 2.2-4.6H15',
  gauge: 'M2.6 12.6a7 7 0 1 1 10.8 0M8 8l3-2.6',
  users: 'M6 8.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2ZM1.4 14a4.6 4.6 0 0 1 9.2 0M11 3.6a2.4 2.4 0 0 1 0 4.8M12.2 9.8A4 4 0 0 1 14.6 14',
  phone: 'M3 2.6h2.4l1.2 3-1.5 1.1a8.4 8.4 0 0 0 4.2 4.2l1.1-1.5 3 1.2v2.4a1 1 0 0 1-1.1 1A11.6 11.6 0 0 1 2 3.7a1 1 0 0 1 1-1.1Z',
  phoneIn: 'M3 2.6h2.4l1.2 3-1.5 1.1a8.4 8.4 0 0 0 4.2 4.2l1.1-1.5 3 1.2v2.4a1 1 0 0 1-1.1 1A11.6 11.6 0 0 1 2 3.7a1 1 0 0 1 1-1.1ZM14 2 9.6 6.4M9.6 2.6v3.8h3.8',
  // The handset of `phone`, tipped down and struck through - the universal
  // hang-up glyph, drawn from the same path so the two read as a pair.
  phoneOff:
    'M3 2.6h2.4l1.2 3-1.5 1.1a8.4 8.4 0 0 0 4.2 4.2l1.1-1.5 3 1.2v2.4a1 1 0 0 1-1.1 1A11.6 11.6 0 0 1 2 3.7a1 1 0 0 1 1-1.1ZM2 14 14 2',
  // An arrow meeting a wall: the call carries on somewhere else.
  forward: 'M2 8h8.4M7.4 4.6 10.8 8l-3.4 3.4M14 3v10',
  list: 'M5.4 4h9M5.4 8h9M5.4 12h9M2 4h.01M2 8h.01M2 12h.01',
  sliders: 'M2 4.6h5M9.6 4.6H14M2 11.4h3.4M8 11.4H14M8.3 2.4v4.4M6.7 9.2v4.4',
  alert: 'M8 2.2 1.6 13.4h12.8L8 2.2ZM8 6.6v3M8 11.6h.01',
  pause: 'M6 3.4v9.2M10 3.4v9.2',
  play: 'M4.6 3.2 12.4 8l-7.8 4.8V3.2Z',
  refresh: 'M13.6 8a5.6 5.6 0 1 1-1.7-4M13.8 2.2v3.6h-3.6',
  check: 'm3 8.4 3.2 3.2L13 4.8',
  x: 'M4 4l8 8M12 4l-8 8',
  chevronRight: 'm6.2 3.4 4.6 4.6-4.6 4.6',
  sun: 'M8 10.8a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6ZM8 1v1.6M8 13.4V15M2.6 2.6l1.1 1.1M12.3 12.3l1.1 1.1M1 8h1.6M13.4 8H15M2.6 13.4l1.1-1.1M12.3 3.7l1.1-1.1',
  moon: 'M13.4 9.6A5.8 5.8 0 0 1 6.4 2.6a5.9 5.9 0 1 0 7 7Z',
  external: 'M9 2.6h4.4V7M13 3 7.6 8.4M11.4 9.4v3.2a.9.9 0 0 1-.9.9h-7a.9.9 0 0 1-.9-.9v-7a.9.9 0 0 1 .9-.9h3.2',
  search: 'M7.4 12.2a4.8 4.8 0 1 0 0-9.6 4.8 4.8 0 0 0 0 9.6ZM13.6 13.6 11 11',
  clock: 'M8 14.2A6.2 6.2 0 1 0 8 1.8a6.2 6.2 0 0 0 0 12.4ZM8 4.6V8l2.2 1.6',
  book: 'M2.2 3.2a1 1 0 0 1 1-1h3.4A1.8 1.8 0 0 1 8 3.4a1.8 1.8 0 0 1 1.4-1.2h3.4a1 1 0 0 1 1 1v8.2a1 1 0 0 1-1 1H9.6A1.8 1.8 0 0 0 8 13.8a1.8 1.8 0 0 0-1.6-1.2H3.2a1 1 0 0 1-1-1V3.2ZM8 3.4v10.4',
  shield: 'M8 1.6 2.8 3.8v4.1c0 3 2.2 5.4 5.2 6.5 3-1.1 5.2-3.5 5.2-6.5V3.8L8 1.6Z',
  terminal: 'm3 4.6 3.2 3.4L3 11.4M8.4 11.6H13',
  key: 'M9.8 6.2a2.8 2.8 0 1 1 3.6 3.6l-.9-.9-1.4 1.4-1.3-1.3L8.4 10.4 2.6 4.6l1.8-1.8 5.4 3.4Z',
  edit: 'M11.4 2.6 13.4 4.6 5.6 12.4 2.6 13.4l1-3L11.4 2.6Z',
  copy: 'M5.4 5.4V3a.8.8 0 0 1 .8-.8h6.6a.8.8 0 0 1 .8.8v6.6a.8.8 0 0 1-.8.8h-2.4M3 6.2h6.6a.8.8 0 0 1 .8.8V13a.8.8 0 0 1-.8.8H3a.8.8 0 0 1-.8-.8V7a.8.8 0 0 1 .8-.8Z',
  clipboard: 'M6 3.2H4.4a.9.9 0 0 0-.9.9v8.8a.9.9 0 0 0 .9.9h7.2a.9.9 0 0 0 .9-.9V4.1a.9.9 0 0 0-.9-.9H10M6 3.2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1M6 3.2v1.2h4V3.2',
  video: 'M2 4.6h7.4a1 1 0 0 1 1 1v4.8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V5.6a1 1 0 0 1 1-1ZM10.4 7l4-2.2v6.4l-4-2.2',
  videoOff: 'M2 4.6h7.4a1 1 0 0 1 1 1v4.8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V5.6a1 1 0 0 1 1-1ZM10.4 7l4-2.2v6.4l-4-2.2M1.4 2 14.6 14',
  mic: 'M8 1.8a2 2 0 0 1 2 2v4a2 2 0 0 1-4 0v-4a2 2 0 0 1 2-2ZM4 7.6a4 4 0 0 0 8 0M8 11.6v2.6M5.8 14.2h4.4',
  micOff: 'M8 1.8a2 2 0 0 1 2 2v4a2 2 0 0 1-4 0v-4a2 2 0 0 1 2-2ZM4 7.6a4 4 0 0 0 8 0M8 11.6v2.6M5.8 14.2h4.4M2 2l12 12',
  screen: 'M1.8 2.8h12.4a.8.8 0 0 1 .8.8v7.2a.8.8 0 0 1-.8.8H1.8a.8.8 0 0 1-.8-.8V3.6a.8.8 0 0 1 .8-.8ZM5.6 14.2h4.8M8 11.6v2.6',
  screenShare: 'M1.8 2.8h12.4a.8.8 0 0 1 .8.8v7.2a.8.8 0 0 1-.8.8H1.8a.8.8 0 0 1-.8-.8V3.6a.8.8 0 0 1 .8-.8ZM5.6 14.2h4.8M8 11.6v2.6M8 9.2V5.2M6.4 6.8 8 5.2l1.6 1.6',
  message: 'M2.4 3h11.2a.9.9 0 0 1 .9.9v6.6a.9.9 0 0 1-.9.9H6.4L3.2 14v-2.6h-.8a.9.9 0 0 1-.9-.9V3.9a.9.9 0 0 1 .9-.9Z',
  record: 'M8 12.6a4.6 4.6 0 1 0 0-9.2 4.6 4.6 0 0 0 0 9.2ZM8 9.6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Z',
  more: 'M3.4 8h.01M8 8h.01M12.6 8h.01',
  userPlus: 'M6 7.6a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8ZM1.4 13.6a4.6 4.6 0 0 1 9.2 0M12.6 5.2v4M10.6 7.2h4',
  grid: 'M2.4 2.4h4.4v4.4H2.4zM9.2 2.4h4.4v4.4H9.2zM2.4 9.2h4.4v4.4H2.4zM9.2 9.2h4.4v4.4H9.2z',
  link: 'M6.8 9.2a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-.7.7M9.2 6.8a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l.7-.7',
  send: 'M14.2 1.8 7 9M14.2 1.8 9.8 14.2 7 9 1.8 6.2l12.4-4.4Z',
  volume: 'M2 6h2.4L8 3v10L4.4 10H2V6ZM10.8 5.6a3.4 3.4 0 0 1 0 4.8M12.6 3.8a6 6 0 0 1 0 8.4',
  expand: 'M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4',
  signal: 'M2.4 13.6v-2M6.1 13.6v-4.4M9.9 13.6V6.8M13.6 13.6V4',
  file: 'M3.4 1.8h6l3.2 3.2v9.2a.8.8 0 0 1-.8.8H3.4a.8.8 0 0 1-.8-.8V2.6a.8.8 0 0 1 .8-.8ZM9.2 1.8v3.4h3.4',
  paperclip: 'M13 7.4 8 12.4a3.2 3.2 0 0 1-4.6-4.6l5.4-5.4a2.1 2.1 0 0 1 3 3L6.4 10.8a1 1 0 0 1-1.4-1.4l4.8-4.8',
  download: 'M8 2.2v8.2M4.8 7.4 8 10.6l3.2-3.2M2.6 13.6h10.8',
  trash: 'M2.6 4.2h10.8M6.2 4.2V2.8h3.6v1.4M3.8 4.2l.7 9.2a.9.9 0 0 0 .9.8h5.2a.9.9 0 0 0 .9-.8l.7-9.2',
  headset: 'M2.4 9.4V8a5.6 5.6 0 0 1 11.2 0v1.4M2.4 9.4h1.8v3.8H3.2a.8.8 0 0 1-.8-.8V9.4ZM13.6 9.4h-1.8v3.8h1a.8.8 0 0 0 .8-.8V9.4Z',
  eye: 'M1.4 8s2.4-4.6 6.6-4.6S14.6 8 14.6 8s-2.4 4.6-6.6 4.6S1.4 8 1.4 8ZM8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  hand: 'M5.4 8.4V3.6a1 1 0 0 1 2 0v4M7.4 7.6V2.8a1 1 0 0 1 2 0v4.8M9.4 7.6V3.6a1 1 0 0 1 2 0v5.6a4.8 4.8 0 0 1-4.8 4.8h-.4a4.4 4.4 0 0 1-3.8-2.2L1.2 8.6a1 1 0 0 1 1.7-1.1l2.5 2.5',
  // A person sharp against a background gone to dots: background blur.
  blur: 'M8 8.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4ZM4.2 13.6a3.8 3.8 0 0 1 7.6 0M1.8 3.4h.01M1.8 8h.01M1.8 12.6h.01M14.2 3.4h.01M14.2 8h.01M14.2 12.6h.01',
};

export function Icon({
  name,
  className,
  size = 16,
}: {
  name: IconName;
  className?: string;
  size?: number;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
